/**
 * captureSchedule — per-cast frame timing derived from the ability's own
 * timeline (DJ 2026-06-12: "frame capture must match expected cast lengths
 * and VFX durations").
 *
 * The fixed [0,250,600,1200] burst spent 3 of 4 frames inside fireball's
 * 1000ms windup and never saw the impact (~1500ms). Instead, anchor frames
 * to the phases the ability actually has:
 *
 *   0 ──────────── cast moment (windup start / instant release)
 *   windUp/2 ───── mid cast-bar (only when there IS a windup)
 *   windUp+pad ─── release / muzzle
 *   +flight/2 ──── mid-flight (projectiles with meaningful travel)
 *   impact+pad ─── impact / hit VFX / FCT
 *   impact+after ─ aftermath (lingering VFX, status auras)
 *   +durationPeek ─ mid-duration (buffs/auras with durationMs)
 *
 * flight = distance/speed (live caster→target distance at cast time),
 * capped by projectileLifespan and a sanity cap. Output is deduped with a
 * minimum gap, clamped, and capped to maxFrames. Pure function — tested
 * directly; AbilityCastCapture feeds it from getAbilityData + the resolved
 * target distance.
 */
import { ABILITY_CAPTURE_CONFIG } from '../../config/diagnostics/abilityCapture.config';

export interface CaptureScheduleInput {
  /** Cast windup in ms (AbilityDataDef.windUpMs). */
  windUpMs?: number;
  /** Ability duration in ms (buffs/auras/channels). */
  durationMs?: number;
  /** Projectile speed in px/s (vfx.projectileSpeed). */
  projectileSpeed?: number;
  /** Projectile lifespan cap in ms (vfx.projectileLifespan). */
  projectileLifespan?: number;
  /** Whether the cast launches a projectile at all. */
  hasProjectile?: boolean;
  /** Live caster→target distance in px at cast time (null = unknown). */
  distancePx?: number | null;
  /** Channeled cast (mechanicType 'channel' / tag 'channel'|'channeled').
   *  Spreads frames across the channel BODY instead of a single mid-duration
   *  peek clamped into the head (K12: a long channel got one ~0ms frame). */
  isChanneled?: boolean;
}

/** Phase label for a frame offset, given the cast's timeline estimates. */
export function captureFramePhase(offsetMs: number, castMs: number, impactMs: number): string {
  if (offsetMs === 0) return 'cast';
  if (castMs > 0 && offsetMs < castMs) return 'windup';
  if (impactMs > castMs && offsetMs < impactMs) return 'flight';
  if (offsetMs <= impactMs + ABILITY_CAPTURE_CONFIG.schedule.impactWindowMs) return 'impact';
  return 'after';
}

export function computeCaptureSchedule(input: CaptureScheduleInput): {
  offsets: number[];
  castMs: number;
  impactMs: number;
} {
  const cfg = ABILITY_CAPTURE_CONFIG.schedule;
  const castMs = Math.max(0, input.windUpMs ?? 0);

  let flightMs = 0;
  if (input.hasProjectile && (input.projectileSpeed ?? 0) > 0) {
    const distance = input.distancePx ?? cfg.defaultTargetDistancePx;
    flightMs = (distance / input.projectileSpeed!) * 1000;
    if (input.projectileLifespan) flightMs = Math.min(flightMs, input.projectileLifespan);
    flightMs = Math.min(flightMs, cfg.flightCapMs);
  }
  const impactMs = castMs + flightMs;

  // A channel's active clock lives in windUpMs (drain_soul: 15000) OR
  // durationMs (tranquility: 8000), whichever is longer — the head-clamped
  // windup/duration-peek anchors would collapse every body sample onto maxMs.
  const channelLen = Math.max(input.durationMs ?? 0, castMs);
  const isChanneled = !!input.isChanneled && channelLen > 0;

  const anchors = [0];
  if (isChanneled) {
    // Body-spread: fixed fractions across the channel body, capped at
    // channelMaxMs so a 15s channel still samples a real window.
    const bodyMax = Math.min(channelLen, cfg.channelMaxMs);
    for (const f of cfg.channelSampleFractions) anchors.push(bodyMax * f);
  } else {
    if (castMs > 0) {
      anchors.push(castMs / 2);            // cast bar mid-fill
      anchors.push(castMs + cfg.releasePadMs); // muzzle / release
    }
    if (flightMs >= cfg.midFlightMinMs) anchors.push(castMs + flightMs / 2);
    anchors.push(impactMs + cfg.impactPadMs);
    anchors.push(impactMs + cfg.aftermathMs);
    if ((input.durationMs ?? 0) > 0) {
      anchors.push(castMs + Math.min(input.durationMs! / 2, cfg.durationPeekCapMs));
    }
  }

  // Round → clamp → sort → dedupe with min gap → cap frame count.
  const clampMax = isChanneled ? Math.max(cfg.maxMs, cfg.channelMaxMs) : cfg.maxMs;
  const sorted = anchors
    .map((a) => Math.min(Math.round(a), clampMax))
    .sort((a, b) => a - b);
  const offsets: number[] = [];
  for (const a of sorted) {
    if (offsets.length === 0 || a - offsets[offsets.length - 1] >= cfg.minGapMs) {
      offsets.push(a);
    }
    if (offsets.length >= cfg.maxFrames) break;
  }
  return { offsets, castMs: Math.round(castMs), impactMs: Math.round(impactMs) };
}
